import { memo } from "react";
import { ScanLine, Loader2 } from "lucide-react";
import { ModePicker } from "@/components/review/ModePicker";
import { DocumentText } from "@/components/review/DocumentText";
import { DocumentPicker } from "@/components/review/DocumentPicker";

const CheckList = ({ items, selected, onToggle, testPrefix, empty, right }) => (
  <div className="border border-slate-800 rounded-md p-2 max-h-16 overflow-y-auto text-xs">
    {items.length ? items.map((x) => (
      <label key={x.id} className="flex items-center gap-2 py-0.5">
        <input type="checkbox" checked={selected.includes(x.id)} onChange={() => onToggle(x.id)} data-testid={`${testPrefix}-${x.id}`} />
        <span className="text-slate-300 truncate">{x.name}</span>
        {right && <span className="text-slate-600 font-mono ml-auto">{right(x)}</span>}
      </label>
    )) : <span className="text-slate-600">{empty}</span>}
  </div>
);

export const DocumentViewer = memo(function DocumentViewer({ docs, viewingDoc, onView }) {
  return (
    <div className="sc-card lg:col-span-6 flex flex-col h-[60vh] lg:h-[82vh]">
      <div className="p-3 border-b border-slate-800 flex items-center gap-2">
        <div className="label-cap">Document</div>
        <select value={viewingDoc?.id || ""} onChange={(e) => onView(e.target.value)} className="bg-slate-950/60 border border-slate-800 rounded-md px-2 py-1 text-xs text-slate-100 flex-1" data-testid="viewing-doc-select">
          {docs.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
      </div>
      <DocumentText content={viewingDoc?.content} />
    </div>
  );
});

export function RunButtons({ running, canRun, onRun, onCancel }) {
  return (
    <div className="mt-4 flex gap-2">
      <button onClick={onRun} disabled={running || !canRun} className={`flex-1 font-semibold py-2.5 rounded-md text-sm inline-flex items-center justify-center gap-2 ${running ? "bg-amber-500/40 text-slate-950" : "bg-amber-500 hover:bg-amber-400 text-slate-950"} disabled:opacity-50`} data-testid="run-ai-scan-button">
        {running ? <><Loader2 className="w-4 h-4 animate-spin" /> Streaming…</> : <><ScanLine className="w-4 h-4" /> Run AI Scan (Streaming)</>}
      </button>
      {running && <button onClick={onCancel} className="border border-slate-700 hover:border-red-500 text-slate-300 px-3 py-2 rounded-md text-sm" data-testid="cancel-scan-button">Cancel</button>}
    </div>
  );
}

export function ReviewControls({ s, mode, setMode, provider, setProvider, custom, setCustom, children }) {
  const toggle = (arr, setArr) => (v) => setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  return (
    <div className="sc-card p-5">
      <div className="label-cap mb-3">Review Mode</div>
      <ModePicker mode={mode} setMode={setMode} docs={s.docs} selectedDocs={s.selectedDocs} />
      {mode === "custom" && <textarea value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="e.g. Flag any spec requiring fabricator to pay for 3rd party CWI inspections…" rows={3} className="mt-3 w-full bg-slate-950/60 border border-slate-800 rounded-md px-3 py-2 text-slate-100 font-mono text-xs focus:outline-none focus:border-amber-500" data-testid="custom-instructions-input" />}
      <div className="mt-4 grid grid-cols-2 gap-3">
        <div>
          <div className="label-cap mb-1">Model</div>
          <select value={provider} onChange={(e) => setProvider(e.target.value)} className="w-full bg-slate-950/60 border border-slate-800 rounded-md px-2 py-2 text-xs text-slate-100" data-testid="model-provider-select">
            <option value="anthropic">Claude Sonnet 5</option><option value="gemini">Gemini 3.1 Pro</option>
          </select>
        </div>
        <div><div className="label-cap mb-1">Playbooks</div><CheckList items={s.playbooks} selected={s.selectedPBs} onToggle={toggle(s.selectedPBs, s.setSelectedPBs)} testPrefix="pb-check" empty="No playbooks" /></div>
      </div>
      <DocumentPicker docs={s.docs} selected={s.selectedDocs} onToggle={toggle(s.selectedDocs, s.setSelectedDocs)} onSelectAll={() => s.setSelectedDocs(s.docs.map((d) => d.id))} onClearAll={() => s.setSelectedDocs([])} />
      {children}
    </div>
  );
}
